import type { ApiErrorFieldValue, ErrorResponse } from "@core/types/errorResponse";
import { isStringArray } from "@core/utils/apiErrorFieldValue";
import {
  DEPENDS_ON_ACTIVITIES,
  ERRORS_DEPENDENTS,
  ERRORS_REASON,
  ERRORS_SUBJECT,
  LABEL_NOT_ON_TEMPLATE,
  LIST_JOIN_FINAL,
  LIST_JOIN_SERIAL,
  REMOVED_ACTIVITIES,
} from "../constants/kpiDependencyConflictConstants";
import type { KpiDependencyConflict } from "../types/kpiDependencyConflict";
import type { KpiDependent, RemovedActivityRef } from "../types/kpi";
import { formatKpiDependentContext } from "./kpiDependentDisplay";

const UNPREFIXED_ERROR_KEYS = new Set(["deductibles"]);
const SKIPPED_ERROR_KEYS = new Set([
  ERRORS_DEPENDENTS,
  ERRORS_REASON,
  ERRORS_SUBJECT,
  "conflicts",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseActivityRefs(raw: unknown): RemovedActivityRef[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((row) => {
      if (!isRecord(row)) return null;
      const activityId = row.activity_id;
      if (typeof activityId !== "number" && typeof activityId !== "string") return null;
      return {
        activity_id: Number(activityId),
        activity_name: typeof row.activity_name === "string" ? row.activity_name : "",
      };
    })
    .filter((x): x is RemovedActivityRef => x !== null);
}

function parseDependent(raw: unknown): KpiDependent | null {
  if (!isRecord(raw)) return null;
  const kpiId = raw.kpi_id;
  if (typeof kpiId !== "number" && typeof kpiId !== "string") return null;
  const kpiName =
    typeof raw.kpi_name === "string" && raw.kpi_name.trim()
      ? raw.kpi_name
      : `KPI #${kpiId}`;

  return {
    kpi_id: Number(kpiId),
    kpi_name: kpiName,
    activity_id: raw.activity_id != null ? Number(raw.activity_id) : null,
    activity_name: typeof raw.activity_name === "string" ? raw.activity_name : null,
    report_template_id:
      raw.report_template_id != null ? Number(raw.report_template_id) : null,
    report_template_name:
      typeof raw.report_template_name === "string" ? raw.report_template_name : null,
    removed_activities: parseActivityRefs(raw[REMOVED_ACTIVITIES]),
    depends_on_activities: parseActivityRefs(raw[DEPENDS_ON_ACTIVITIES]),
  };
}

export function kpiConflictFromError(error: ErrorResponse | null | undefined): KpiDependencyConflict | null {
  if (!error?.errors) return null;
  const dependentsRaw = error.errors[ERRORS_DEPENDENTS];
  if (!Array.isArray(dependentsRaw) || dependentsRaw.length === 0) return null;

  const dependents = dependentsRaw
    .map(parseDependent)
    .filter((d): d is KpiDependent => d !== null);
  if (dependents.length === 0 && dependentsRaw.length > 0) {
    return {
      reason: typeof error.errors[ERRORS_REASON] === "string" ? error.errors[ERRORS_REASON] : undefined,
      subject: parseSubject(error.errors[ERRORS_SUBJECT]),
      dependents: [],
      detail: error.detail ?? "",
    };
  }
  if (dependents.length === 0) return null;

  return {
    reason: typeof error.errors[ERRORS_REASON] === "string" ? error.errors[ERRORS_REASON] : undefined,
    subject: parseSubject(error.errors[ERRORS_SUBJECT]),
    dependents,
    detail: error.detail ?? "",
  };
}

function parseSubject(value: ApiErrorFieldValue | undefined): KpiDependencyConflict["subject"] {
  if (!isRecord(value)) return undefined;
  const subject: Record<string, string | number | null | undefined> = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof val === "string" || typeof val === "number" || val === null || val === undefined) {
      subject[key] = val;
    }
  }
  return subject;
}

export function stringValidationMessagesFromErrors(
  errors: Record<string, ApiErrorFieldValue> | undefined
): string[] {
  if (!errors) return [];
  const messages: string[] = [];
  for (const [field, value] of Object.entries(errors)) {
    if (SKIPPED_ERROR_KEYS.has(field)) continue;
    if (isStringArray(value)) {
      for (const msg of value) {
        messages.push(UNPREFIXED_ERROR_KEYS.has(field) ? msg : `${field}: ${msg}`);
      }
    }
  }
  return messages;
}

export function errorsHasUnhandledNonStringValues(
  errors: Record<string, ApiErrorFieldValue> | undefined
): boolean {
  if (!errors) return false;
  for (const [field, value] of Object.entries(errors)) {
    if (SKIPPED_ERROR_KEYS.has(field)) continue;
    if (isStringArray(value)) continue;
    if (typeof value === "string") continue;
    if (field === ERRORS_DEPENDENTS && Array.isArray(value)) continue;
    if (value !== undefined && value !== null) return true;
  }
  return false;
}

export function formatEnglishList(names: string[]): string {
  const filtered = names.filter(Boolean);
  if (filtered.length === 0) return "";
  if (filtered.length === 1) return filtered[0];
  if (filtered.length === 2) return `${filtered[0]}${LIST_JOIN_FINAL}${filtered[1]}`;
  return `${filtered.slice(0, -1).join(LIST_JOIN_SERIAL)}, and ${filtered[filtered.length - 1]}`;
}

export function formatKpiUsesLine(names: string[]): string | null {
  const list = formatEnglishList(names);
  if (!list) return null;
  return list;
}

export function formatKpiConflictBulletLines(
  dependent: KpiDependent,
  _reason: string | undefined
): string[] {
  const context = formatKpiDependentContext(dependent);
  if (context) return [context];
  return [LABEL_NOT_ON_TEMPLATE];
}
